import {Navigate, useOutlet, useNavigate, useLocation, Link} from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {BankOutlined, LogoutOutlined, SettingOutlined, CalendarOutlined, CameraOutlined, ControlOutlined} from '@ant-design/icons';
import { MenuProps, Layout, Menu, theme } from 'antd';
import {FormattedMessage} from "react-intl";
import { UserRoleEnum } from "../enums/UserRoleEnum";
import styled from "styled-components";

const { Header, Footer, Sider } = Layout;

type MenuItem = Required<MenuProps>['items'][number];

function getItem(
    label: React.ReactNode,
    key: React.Key,
    icon?: React.ReactNode,
    children?: MenuItem[],
): MenuItem {
    return {
        key,
        icon,
        children,
        label,
    } as MenuItem;
}

const StyledLayout = styled(Layout)`
  min-height: 100vh;
  background-color: #f0f5ff;
`;

const StyledSider = styled(Sider)`
  background: linear-gradient(180deg, #1a2847 0%, #0f1727 100%) !important;
  
  .ant-layout-sider-trigger {
    background: #1a2847 !important;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
  }
  
  .ant-menu {
    background: transparent !important;
  }
  
  .ant-menu-item {
    margin: 4px 8px !important;
    border-radius: 8px;
    transition: all 0.3s ease;
    
    &:hover {
      background: rgba(65, 105, 225, 0.2) !important;
    }
    
    &.ant-menu-item-selected {
      background: linear-gradient(135deg, #4169e1 0%, #1890ff 100%) !important;
      
      &::after {
        display: none;
      }
    }
  }
  
  .ant-menu-item-icon {
    font-size: 18px;
  }
`;

const LogoWrapper = styled.div`
  padding: 24px 16px;
  text-align: center;
  margin-bottom: 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const LogoLink = styled(Link)`
  display: inline-block;
  text-decoration: none;
  
  &:hover {
    opacity: 0.9;
  }
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 22px;
  font-weight: bold;
  
  .anticon {
    color: #4169e1;
    margin-right: 8px;
    font-size: 24px;
  }
`;

const StyledHeader = styled(Header)`
  background: white !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 0 24px !important;
  height: 64px;
  display: flex;
  align-items: center;
  border-bottom: 1px solid #f0f0f0;
`;

const StyledFooter = styled(Footer)`
  text-align: center;
  background: white;
  border-top: 1px solid #f0f0f0;
  color: #666;
  padding: 16px 24px;
`;

const ContentLayout = styled(Layout)`
  background: #f0f5ff;
`;

const MenuSection = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 200px);
  justify-content: space-between;
`;

export const ProtectedLayout = () => {
    const { user, logout, isSuperAdmin } = useAuth();
    const outlet = useOutlet();
    const [collapsed, setCollapsed] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    // Redirect super admins to their dashboard
    useEffect(() => {
        if (user && isSuperAdmin()) {
            navigate("/admin/dashboard", { replace: true });
        }
    }, [user, navigate, isSuperAdmin]);

    const handleLogout = useCallback(() => {
        logout();
    }, [logout]);

    const items: MenuItem[] = useMemo(() => {
        const returnValue = [];
        const isAdmin = user?.roles?.includes(UserRoleEnum.OrganisationAdmin) || 
                      user?.roles?.includes(UserRoleEnum.SuperAdmin);

        // Always show Events at the top for all users
        returnValue.push(getItem(<FormattedMessage id={'section.eventsName'} />, 'events', <CalendarOutlined />));
        
        // Hide Dashboard item - removed
        // returnValue.push(getItem(<FormattedMessage id={'section.dashboardName'} />, 'dashboard', <PieChartOutlined />));
        
        returnValue.push(getItem(<FormattedMessage id={'section.organisationName'} />, 'organisation', <BankOutlined />));
        returnValue.push(getItem(<FormattedMessage id={'section.profileName'} />, 'profile', <SettingOutlined />));

        // Add admin link for super admins
        if (isSuperAdmin()) {
            returnValue.push(getItem(<FormattedMessage id={'section.administrationName'} defaultMessage="Administration" />, 'administration', <ControlOutlined />));
        }

        return returnValue;
    }, [user, isSuperAdmin]);

    const bottomItems: MenuItem[] = useMemo(() => {
        const returnValue = [];

        returnValue.push(getItem(<FormattedMessage id={'section.signoutName'} />, 'signout', <LogoutOutlined />));

        return returnValue;
    }, []);

    const onClick: MenuProps['onClick'] = (e) => {
        if (e.key === "signout") {
            handleLogout();
        } else if (e.key === 'profile') {
            navigate("/dashboard/profile", { replace: true });
        } else if (e.key === 'organisation') {
            navigate("/dashboard/organisation", { replace: true });
        } else if (e.key === 'events') {
            // For events or dashboard, always navigate to events
            navigate("/dashboard/events", { replace: true });
        } else if (e.key === 'dashboard') {
            // Redirect to events instead
            navigate("/dashboard/events", { replace: true });
        } else if (e.key === 'administration') {
            // Redirect to admin dashboard
            navigate("/admin/dashboard", { replace: true });
        }
    };

    const selectedKeys = useMemo(() => {
        const returnValue = [];

        // Check if path starts with '/dashboard/events' to highlight events menu item
        if (location.pathname.startsWith('/dashboard/events')) {
            returnValue.push('events');
        } 
        // Legacy gallery URLs - highlight events menu for these too
        else if (location.pathname.startsWith('/dashboard/galleries')) {
            returnValue.push('events');
        } 
        else if (location.pathname === '/dashboard/profile') {
            returnValue.push('profile');
        } else if (location.pathname === '/dashboard/organisation') {
            returnValue.push('organisation');
        } 
        // Default to events for plain '/dashboard' path
        else if (location.pathname === '/dashboard') {
            returnValue.push('events');
        }
        // For any other paths, highlight events as default
        else {
            returnValue.push('events');
        }

        return returnValue;
    }, [location.pathname]);

    if (!user) {
        return <Navigate to="/sign-in" />;
    }

    // Redirect super admins to admin dashboard
    if (isSuperAdmin()) {
        return <Navigate to="/admin/dashboard" />;
    }

    return (
        <StyledLayout>
            <StyledSider collapsible collapsed={collapsed} onCollapse={(value) => setCollapsed(value)}>
                <LogoWrapper>
                    <LogoLink to="/">
                        {collapsed ? (
                            <CameraOutlined style={{ color: '#4169e1', fontSize: '24px' }} />
                        ) : (
                            <Logo>
                                <CameraOutlined />
                                RecogniX
                            </Logo>
                        )}
                    </LogoLink>
                </LogoWrapper>
                <MenuSection>
                    <Menu
                        onClick={onClick}
                        theme="dark"
                        selectedKeys={selectedKeys}
                        mode="inline"
                        items={items}
                    />
                    <Menu
                        onClick={onClick}
                        style={{ marginBottom: '16px' }}
                        theme="dark"
                        mode="inline"
                        items={bottomItems}
                        selectable={false}
                    />
                </MenuSection>
            </StyledSider>
            <ContentLayout>
                <StyledHeader />
                {outlet}
                <StyledFooter>
                    RecogniX.com.au © {new Date().getFullYear()}
                </StyledFooter>
            </ContentLayout>
        </StyledLayout>
    );
};